import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Download } from "lucide-react";
import Reveal from "@/components/Reveal";
import { IMAGES } from "@/lib/constants";
import { submitInvestorInquiry, formatApiError, pitchDeckDownloadUrl } from "@/lib/api";

const INVESTOR_TYPES = ["Local Investor", "International Investor", "Family Office", "Institutional", "Diaspora / Returning Investor"];
const TICKETS = ["Under $50K", "$50K – $250K", "$250K – $1M", "$1M – $5M", "$5M+"];
const PACKAGES = ["Flat fee (US$15K – US$50K)", "Equity / Profit share", "Joint venture", "Not sure — advise"];
const PROJECTS = ["Reign Villa Resorts (Canelles, St. Lucia)", "Alberta multi-family", "Property flip pool", "Land assembly", "General inquiry"];

const HOW_STEPS = [
  { no: "01", title: "Introduction", desc: "A first call to understand your goals, timeline, risk profile, and preferred markets." },
  { no: "02", title: "Opportunity Match", desc: "We share vetted current and pipeline opportunities that fit your mandate." },
  { no: "03", title: "Structure", desc: "Choose your engagement — flat fee, equity, or joint venture — with legal support from our partners." },
  { no: "04", title: "Deploy", desc: "Capital deployed. Regular reporting. Milestones tracked. Exit on plan." },
];

const RETURNS = [
  { label: "Property flip ROI target", value: "20 – 40%", note: "Per project, net of fees" },
  { label: "Development margin", value: "25 – 35%", note: "On ground-up builds" },
  { label: "Rental yield (mgmt fee)", value: "8 – 12%", note: "Of gross rent, ongoing" },
  { label: "Hold period (typical)", value: "5 – 18 months", note: "For flips; 24 – 36 mo for dev" },
];

export default function Invest() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    country: "",
    investor_type: INVESTOR_TYPES[1],
    project_interest: PROJECTS[0],
    ticket_size: TICKETS[2],
    package_preference: PACKAGES[3],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.email) {
      setError("Please provide your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await submitInvestorInquiry(form);
      setSuccess(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="invest-page" className="bg-[#0A1628]">
      {/* HEADER */}
      <section data-testid="invest-header" className="relative pt-40 pb-20 md:pb-24 px-6 md:px-12 lg:px-16 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.stlucia} alt="Caribbean" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628]/70 via-[#0A1628]/85 to-[#0A1628]" />
        </div>
        <div className="relative max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-2">
            <div className="overline">Invest</div>
          </div>
          <div className="lg:col-span-10">
            <h1 className="font-serif-display text-6xl md:text-8xl lg:text-[8.5rem] text-[#F3EFE4] leading-[0.88]">
              Put your capital<br />
              to work — <span className="italic text-[#D4AF37]">with a partner.</span>
            </h1>
            <p className="mt-10 max-w-xl text-[#8FA3B8] leading-relaxed text-base md:text-lg">
              U-NEXX operates a hybrid platform: service fees for hands-off clients, and profit-sharing / joint ventures for investors who want exposure to Alberta and Caribbean real estate without operational overhead.
            </p>
          </div>
        </div>
      </section>

      {/* RETURN METRICS */}
      <section data-testid="invest-returns" className="py-16 md:py-20 px-6 md:px-12 lg:px-16 border-y border-white/10">
        <div className="max-w-[1400px] mx-auto grid md:grid-cols-4 gap-px bg-white/10">
          {RETURNS.map((r) => (
            <div key={r.label} className="bg-[#0A1628] p-8">
              <div className="overline !text-[0.62rem] mb-4">{r.label}</div>
              <div className="font-serif-display text-4xl md:text-5xl text-[#D4AF37] mb-2">{r.value}</div>
              <div className="text-xs text-[#8FA3B8]">{r.note}</div>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section data-testid="invest-how" className="py-24 md:py-32 px-6 md:px-12 lg:px-16">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-5">
              <div className="overline mb-6">How it works</div>
              <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[#F3EFE4] leading-[0.95]">
                Four steps from<br />
                <span className="italic text-[#D4AF37]">interest to allocation.</span>
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 self-end">
              <p className="text-[#8FA3B8] leading-relaxed">
                Every investor journey is different — but we've refined our onboarding into four clear steps to keep it fast, transparent, and low-friction.
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 border-t border-l border-white/10">
            {HOW_STEPS.map((s, i) => (
              <Reveal key={s.no} delay={i}>
                <div className="border-r border-b border-white/10 p-8 md:p-10 min-h-[240px] flex flex-col justify-between">
                  <div className="font-serif-display text-3xl text-[#D4AF37]">{s.no}</div>
                  <div>
                    <h3 className="font-serif-display text-2xl text-[#F3EFE4] mb-3">{s.title}</h3>
                    <p className="text-sm text-[#8FA3B8] leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PACKAGES */}
      <section data-testid="invest-packages" className="py-24 md:py-32 px-6 md:px-12 lg:px-16 bg-[#0F1F35]">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-5">
              <div className="overline mb-6">Engagement models</div>
              <h2 className="font-serif-display text-4xl md:text-5xl text-[#F3EFE4] leading-[0.95]">
                Two ways to invest with us.
              </h2>
            </div>
          </div>
          <div className="grid lg:grid-cols-2 gap-6 md:gap-8">
            <PackageCard
              tag="Concierge · Service Fee"
              title="Managed Investment"
              price="US$15K – US$50K"
              priceNote="Flat fee, or 5% – 10% of total investment"
              desc="Best for international investors who want a hands-off experience. We source, acquire, develop, and manage — you receive quarterly reports and returns."
              includes={[
                "Property sourcing & acquisition",
                "Legal, tax & currency coordination",
                "Full development & construction oversight",
                "Ongoing property management",
                "Quarterly transparency reports",
              ]}
            />
            <PackageCard
              tag="Equity · Joint Venture"
              title="Profit-Share Partnership"
              price="Bespoke"
              priceNote="Structured per project"
              desc="Best for investors who want direct exposure to our project economics. Structured as SPV equity or JV, with pro-rata profit share on exit."
              includes={[
                "Direct equity in project SPV",
                "Pro-rata share of net project profit",
                "Board or observer rights (case-by-case)",
                "Access to co-investment opportunities",
                "Full financial + development reporting",
              ]}
              gold
            />
          </div>
          <p className="text-xs text-[#8FA3B8] mt-8 max-w-2xl leading-relaxed">
            All engagements begin with a discovery call and are subject to KYC, jurisdiction eligibility, and mutual fit. Historical performance targets shown above are not a guarantee of future results.
          </p>
        </div>
      </section>

      {/* FORM */}
      <section data-testid="invest-form" className="py-24 md:py-32 px-6 md:px-12 lg:px-16">
        <div className="max-w-[1100px] mx-auto">
          {!success ? (
            <div className="grid lg:grid-cols-12 gap-12">
              <div className="lg:col-span-5">
                <div className="overline mb-6">Investor inquiry</div>
                <h2 className="font-serif-display text-4xl md:text-5xl text-[#F3EFE4] leading-[0.95] mb-6">
                  Start a<br /><span className="italic text-[#D4AF37]">conversation.</span>
                </h2>
                <p className="text-[#8FA3B8] leading-relaxed mb-6">
                  Share a few details and a partner will contact you within 24 hours to schedule a discovery call.
                </p>
                <a
                  href={pitchDeckDownloadUrl}
                  data-testid="invest-download-deck"
                  target="_blank"
                  rel="noreferrer"
                  className="btn-secondary !mt-4"
                >
                  Download Pitch Deck <Download size={14} />
                </a>
              </div>

              <form onSubmit={handleSubmit} className="lg:col-span-7 bg-[#0F1F35] border border-white/10 p-8 md:p-10" data-testid="invest-form-el">
                <div className="grid md:grid-cols-2 gap-x-8 gap-y-7">
                  <div>
                    <label className="field-label" htmlFor="iv-name">Full Name *</label>
                    <input id="iv-name" data-testid="invest-input-name" className="field-input" value={form.name} onChange={update("name")} required />
                  </div>
                  <div>
                    <label className="field-label" htmlFor="iv-email">Email *</label>
                    <input id="iv-email" type="email" data-testid="invest-input-email" className="field-input" value={form.email} onChange={update("email")} required />
                  </div>
                  <div>
                    <label className="field-label" htmlFor="iv-phone">Phone</label>
                    <input id="iv-phone" data-testid="invest-input-phone" className="field-input" value={form.phone} onChange={update("phone")} />
                  </div>
                  <div>
                    <label className="field-label" htmlFor="iv-country">Country of residence</label>
                    <input id="iv-country" data-testid="invest-input-country" className="field-input" value={form.country} onChange={update("country")} />
                  </div>
                  <div>
                    <label className="field-label" htmlFor="iv-type">Investor Profile</label>
                    <select id="iv-type" data-testid="invest-input-type" className="field-input" value={form.investor_type} onChange={update("investor_type")}>
                      {INVESTOR_TYPES.map((t) => <option key={t} value={t} className="bg-[#0F1F35]">{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="field-label" htmlFor="iv-project">Project Interest</label>
                    <select id="iv-project" data-testid="invest-input-project" className="field-input" value={form.project_interest} onChange={update("project_interest")}>
                      {PROJECTS.map((p) => <option key={p} value={p} className="bg-[#0F1F35]">{p}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="field-label" htmlFor="iv-ticket">Investment size</label>
                    <select id="iv-ticket" data-testid="invest-input-ticket" className="field-input" value={form.ticket_size} onChange={update("ticket_size")}>
                      {TICKETS.map((t) => <option key={t} value={t} className="bg-[#0F1F35]">{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="field-label" htmlFor="iv-package">Preferred structure</label>
                    <select id="iv-package" data-testid="invest-input-package" className="field-input" value={form.package_preference} onChange={update("package_preference")}>
                      {PACKAGES.map((p) => <option key={p} value={p} className="bg-[#0F1F35]">{p}</option>)}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="field-label" htmlFor="iv-message">Anything else we should know?</label>
                    <textarea id="iv-message" rows={4} data-testid="invest-input-message" className="field-input resize-none" value={form.message} onChange={update("message")} />
                  </div>
                </div>

                {error && (
                  <div data-testid="invest-error" className="mt-6 text-sm text-red-400 border border-red-500/30 px-4 py-3">{error}</div>
                )}

                <button type="submit" data-testid="invest-submit" disabled={submitting} className="btn-primary mt-8 w-full justify-center disabled:opacity-50">
                  {submitting ? "Sending…" : "Submit Investor Inquiry"}
                  {!submitting && <span className="btn-arrow">→</span>}
                </button>
              </form>
            </div>
          ) : (
            <motion.div data-testid="invest-success" className="max-w-2xl mx-auto text-center py-20" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="mx-auto mb-8 w-20 h-20 border border-[#D4AF37] flex items-center justify-center">
                <Check className="text-[#D4AF37]" size={32} />
              </div>
              <div className="overline mb-4">Inquiry received</div>
              <h2 className="font-serif-display text-4xl md:text-5xl text-[#F3EFE4] mb-6">Thank you.</h2>
              <p className="text-[#8FA3B8] max-w-md mx-auto mb-10 leading-relaxed">
                A partner will personally reach out within 24 hours to schedule your discovery call. In the meantime, feel free to download our investor pitch deck.
              </p>
              <a href={pitchDeckDownloadUrl} data-testid="invest-success-deck" target="_blank" rel="noreferrer" className="btn-primary">
                Download Pitch Deck <Download size={16} />
              </a>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
}

function PackageCard({ tag, title, price, priceNote, desc, includes, gold }) {
  return (
    <div className={`p-8 md:p-10 border ${gold ? "border-[#D4AF37]/50 bg-[#D4AF37]/[0.04]" : "border-white/10 bg-[#0A1628]"}`}>
      <div className={`overline mb-4 ${gold ? "!text-[#D4AF37]" : ""}`}>{tag}</div>
      <h3 className="font-serif-display text-3xl md:text-4xl text-[#F3EFE4] mb-4">{title}</h3>
      <div className="font-serif-display text-3xl text-[#D4AF37] leading-none">{price}</div>
      <div className="text-xs text-[#8FA3B8] mt-2 mb-6">{priceNote}</div>
      <p className="text-sm text-[#8FA3B8] leading-relaxed mb-6">{desc}</p>
      <ul className="space-y-3">
        {includes.map((i) => (
          <li key={i} className="flex items-start gap-3 text-sm text-[#F3EFE4]">
            <span className="mt-2 inline-block w-3 h-px bg-[#D4AF37] shrink-0" />
            <span>{i}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
